import { isAnnulOperation } from '@festina/configuration-general-types/configuration'
import type { BranchKey, LayerKey, WorkspaceKey } from '@festina-life/api'
import { assertNotEmpty, dateUtils, type Optional } from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'

import { useParameterContext } from '../../context/parameterContext'
import { configurationItem } from '../../system/paths'
import type { ConfigurationItem } from '../../types/common'
import { isParameterValueItem } from '../../uiModels/parameterOperations/parameterConfigurationItems'
import type { ChangeOperation } from '../../uiModels/workspace/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { Attribute } from '../attribute/Attribute'
import { BlockIndicator } from '../blockIndicator/BlockIndicator'
import { ErrorCount } from '../errorCount/ErrorCount'
import { LayerIndicator } from '../layerIndicator/LayerIndicator'
import { TemplateConflictsInWorkspace } from '../templateConflicts/TemplateConflictsInWorkspace'
import { OperationLink } from './OperationLink'

type Props = {
  itemId: string
  itemLabel?: Optional<string>
  isLoading?: boolean
  errorCount?: number
  fromDate?: Optional<Date>
  toDate?: Optional<Date>
  branchKey: Optional<BranchKey>
  workspaceKey: Optional<WorkspaceKey>
  type: ConfigurationItem
  /** Overrides the icon shown. Defaults to `type`. */
  iconType?: ConfigurationItem
  operationKey?: Optional<string>
  operation?: ChangeOperation
  conflictCount?: number
  block: string
  layer: LayerKey
  isDeleteEnabled?: boolean
  untilChangesetKey?: Optional<string>
  propertyPath?: Optional<string>
  shouldShowLocationIndicators?: boolean
  isDraft: boolean
}

export const LocationAwareOperationLink = ({
  itemId,
  itemLabel,
  isLoading,
  errorCount,
  fromDate,
  toDate,
  workspaceKey,
  branchKey,
  type,
  iconType,
  operation,
  operationKey,
  conflictCount,
  block,
  layer,
  isDeleteEnabled = true,
  untilChangesetKey,
  propertyPath,
  shouldShowLocationIndicators = true,
  isDraft,
}: Props) => {
  const { onRemoveOperation } = useOperationsContext()
  const { onReviewOperation } = useParameterContext()

  const navigationPath = configurationItem({
    block,
    layer,
    itemId,
    workspaceName: workspaceKey?.name,
    server: (workspaceKey?.branchKey.server ?? branchKey?.server)!,
    branch: branchKey?.name,
    type,
    untilChangesetKey,
    path: propertyPath,
  })

  const handleLinkClicked = () => {
    if (isParameterValueItem(type) && operationKey) {
      onReviewOperation(operationKey)
    }
  }

  const handleRemoveOperation = async () => {
    assertNotEmpty(operation)
    await onRemoveOperation(operation)
  }

  return (
    <div className="group flex w-full gap-x-2">
      <OperationLink
        to={navigationPath}
        onClick={handleLinkClicked}
        label={itemLabel ?? itemId}
        type={type}
        isAnnulledValue={
          isAnnulOperation(operation) &&
          operation.createOperationClassName === 'ParameterValueCreateOperation'
        }
        iconType={iconType}
        isDraft={isDraft}
      >
        {fromDate && !toDate && (
          <Attribute>{`${dateUtils.dateAsStr(fromDate)}${type === 'parameterValue' ? ' - ' : ''}`}</Attribute>
        )}
        {fromDate && toDate && (
          <Attribute>{`${dateUtils.dateAsStr(fromDate)} - ${dateUtils.dateAsStr(toDate)}`}</Attribute>
        )}
        {shouldShowLocationIndicators && (
          <>
            <BlockIndicator
              block={block}
              shouldShowIcon={false}
              isSecondaryBackground={false}
            />
            <LayerIndicator layer={layer} isSecondaryBackground={false} />
          </>
        )}
        {errorCount !== undefined && (
          <ErrorCount count={errorCount} isLoading={!!isLoading} />
        )}
        {(type === 'template' || type === 'library') && workspaceKey && (
          <TemplateConflictsInWorkspace count={conflictCount ?? 0} />
        )}
      </OperationLink>
      {!!operation && isDeleteEnabled && (
        <IconButton
          iconName="Trash"
          isDanger
          iconSize="sm"
          className="invisible group-hover:visible"
          onClick={handleRemoveOperation}
        />
      )}
    </div>
  )
}
